import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { PLANET_GLYPHS, PLANET_EN_ES, ASPECT_EN_ES, TARGET_ES } from "../lib/api";
import { useContent } from "../lib/content";

const fmt = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" }) : null;

function EventRow({ e }) {
  return (
    <div data-testid={`timeline-event-${e.planet}-${e.target}-${e.aspect}`.toLowerCase()}
      className={`grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-2 py-4 border-b border-white/5 ${e.active_now ? "pl-3 border-l-2 border-l-[#C9A25E]" : ""}`}>
      <div className="flex items-center gap-3">
        <span className="text-[#C9A25E] text-xl w-6 text-center">{PLANET_GLYPHS[e.planet]}</span>
        <span className="text-white">{PLANET_EN_ES[e.planet]}</span>
        <span className="text-[#C9A25E] text-sm">{ASPECT_EN_ES[e.aspect]}</span>
        <span className="text-white">{TARGET_ES[e.target]}</span>
      </div>
      <div className="text-sm text-zinc-400">
        <span>{e.start ? `Desde ${fmt(e.start)}` : "En curso desde hace más de un año"}</span>
        <span className="mx-2 text-zinc-600">→</span>
        <span>{e.end ? `hasta ${fmt(e.end)}` : "continúa más allá de 2 años"}</span>
        {e.active_now && <span className="ml-3 text-xs text-[#C9A25E]">● activo ahora</span>}
        {e.next_entry && <span className="ml-3 text-xs text-amber-200/80">○ vuelve a orbe el {fmt(e.next_entry)}</span>}
        {e.exact.length > 0 && (
          <div className="text-xs mt-1 text-zinc-300">Exacto: {e.exact.map(fmt).join(" · ")}</div>
        )}
        {e.passes.length > 1 && (
          <div className="text-xs mt-1 text-zinc-500">
            {e.passes.length} pasadas (por retrogradación): {e.passes.map((p) => `${fmt(p.start) || "…"} – ${fmt(p.end) || "…"}`).join(" · ")}
          </div>
        )}
      </div>
    </div>
  );
}

function Ingresses({ items }) {
  const { t } = useContent();
  if (!items.length) return null;
  return (
    <div data-testid="timeline-ingresses" className="mt-12">
      <p className="overline mb-4">{t("plat_ingress_title", "Ingresos a tus casas")}</p>
      {items.map((g) => (
        <div key={g.planet + g.house + g.date} data-testid={`ingress-${g.planet.toLowerCase()}-${g.house}`}
          className="py-3 border-b border-white/5 text-sm flex flex-wrap gap-x-4 gap-y-1">
          <span className="text-white"><span className="text-[#C9A25E] mr-2">{PLANET_GLYPHS[g.planet]}</span>
            {PLANET_EN_ES[g.planet]} entra en tu Casa {g.house}</span>
          <span className="text-zinc-300">{fmt(g.date)}</span>
          <span className="text-zinc-500">Muy intensa hasta {fmt(g.very_intense_until) || "—"} · Intensa hasta {fmt(g.intense_until) || "más allá de 2 años"}</span>
        </div>
      ))}
    </div>
  );
}

function Collapsible({ testid, title, count, gold, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} className={gold ? "border border-[#C9A25E]/30 bg-[#C9A25E]/[0.03] p-6 mb-10" : "mb-10"}>
      <button data-testid={`${testid}-toggle`} onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 text-left group">
        <span className="overline">{title} <span className="text-zinc-500 ml-2">· {count}</span></span>
        <ChevronDown size={18} className={`text-[#C9A25E] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div data-testid={`${testid}-content`} className="mt-2">{children}</div>}
    </div>
  );
}

export default function TransitTimeline({ timeline }) {
  const { t } = useContent();
  const soon = timeline.events.filter((e) => e.soon);
  const later = timeline.events.filter((e) => !e.soon);
  return (
    <div data-testid="transit-timeline" className="mt-16">
      <h3 className="font-display text-3xl text-white font-light mb-2">{t("plat_timeline_title", "Línea de tránsitos · próximos 12 meses")}</h3>
      <p className="text-zinc-500 text-sm mb-8">Del {fmt(timeline.today)} al {fmt(timeline.until)}.</p>
      <Collapsible testid="timeline-soon" gold title={t("plat_timeline_soon_title", "Próximos 6 meses")} count={soon.length}>
        {soon.length ? soon.map((e, i) => <EventRow key={i} e={e} />) : <p className="text-zinc-500 text-sm">Sin tránsitos en este periodo.</p>}
      </Collapsible>
      <Collapsible testid="timeline-later" title={t("plat_timeline_later_title", "De 6 a 12 meses")} count={later.length}>
        {later.length ? later.map((e, i) => <EventRow key={i} e={e} />) : <p className="text-zinc-500 text-sm">Sin tránsitos nuevos en este periodo.</p>}
      </Collapsible>
      <Ingresses items={timeline.ingresses} />
    </div>
  );
}
